import { UserData } from '../../../shared/typings/main';
import { CommunicationChannel, TabData, UserConsentRecord } from '../types/types';

/**
 * Per-purpose, per-channel opt-in state the user is currently editing.
 * Keyed by purposeId, then by channelName (matches ChannelState.channelName).
 */
export type CheckboxState = Record<string, Record<string, boolean>>;

export interface ConsentAddressPayload {
  partialAddress: 'Y' | 'N';
  addressType: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  provinceStateCode: string;
  country: string;
  postalCode: string;
  deliveryPointValidation: 'Y' | 'N';
  deliverabilityScore: string;
  valid: 'Y' | 'N';
  lastUpdatedDateTime: string;
}

export interface ConsentChannelPayload {
  channelId: string;
  channelValue: string;
  optIn: 'Y' | 'N';
  /** Only present on the Direct Mail channel — required by createConsent whenever it's included. */
  address?: ConsentAddressPayload;
}

export interface ConsentPurposePayload {
  purposeId: string;
  /**
   * NOT currently sent — confirmed via a real backend rejection that the
   * createCustomerConsent mutation's schema does not accept this field at
   * all. It's intended for updateConsent (which is broken server-side, see
   * buildConsentPayload's docstring), so it's kept here, unused, ready to
   * wire back in once updateConsent is fixed and we route resaves through it.
   */
  transactionId?: string;
}

export interface ConsentEntryPayload {
  purpose: ConsentPurposePayload;
  channels: ConsentChannelPayload[];
}

export interface ConsentEmailPayload {
  type: 'EMAIL_1';
  address: string;
  lastUpdatedDateTime: string;
  valid: 'Y' | 'N';
}

export interface ConsentCustomerPayload {
  vwgoaNarId: string;
  externalId: {
    sourceAppCustId: string;
    sourceAppName: 'MY_VW';
  };
  lastName: string;
  firstName: string;
  email: ConsentEmailPayload[];
  nameLastUpdatedDateTime: string;
}

export interface ConsentMutationInput {
  customer: ConsentCustomerPayload;
  consentSourceId: string;
  consentDateTime: string;
  consents: ConsentEntryPayload[];
}

export interface ConsentMutationPayload {
  input: ConsentMutationInput;
}

// Fixed value confirmed with backend — same for every consent mutation.
const CONSENT_SOURCE_ID = '854eda3b-af41-47b8-ae7e-9069d14a69f1';
const SOURCE_APP_NAME = 'MY_VW';
const DIRECT_MAIL_CHANNEL_NAME = 'Direct Mail';

/**
 * Small, fast, non-cryptographic string hash (djb2 variant). Not used for
 * anything security-sensitive — only to deterministically derive stable ids
 * from a seed string so the same input always produces the same output,
 * with no backend round-trip or client-side persistence required.
 */
const hashToHex = (seed: string): string => {
  let hash = 5381;

  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 33) ^ seed.charCodeAt(i);
  }

  // Force unsigned 32-bit, then to hex — always 8 characters, zero-padded.
  return (hash >>> 0).toString(16).padStart(8, '0');
};

/**
 * ECM rejects ISO timestamps that include milliseconds — confirmed via a
 * real backend error: `Unparseable date: "2026-08-04T18:50:58.331Z"`.
 * `Date.prototype.toISOString()` always includes milliseconds, so every
 * timestamp sent to this API must go through this helper first to strip
 * them, producing the `YYYY-MM-DDTHH:mm:ssZ` format the backend expects.
 */
export const toEcmDateTime = (date: Date): string => {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

/**
 * updateConsent requires a mandatory transactionId per purpose. There is no
 * backend-issued value available today, so we derive a stable id per
 * user+purpose the same way as sourceAppCustId — reproducible without any
 * persistence, and stable across repeated updates for the same purpose.
 */
export const deriveTransactionId = (userId: string, purposeId: string): string => {
  return hashToHex(`${userId.toUpperCase()}:${purposeId}`);
};

// UserData.address.country stores the 2-letter ISO code (e.g. "US", "CA"),
// but ECM's address object requires the 3-letter form — confirmed via a
// real backend rejection of "US" where "USA" was expected. This app only
// ever deals with US and CA markets, so a small fixed map is sufficient;
// anything else falls back to the original value unchanged.
// NOTE: "CAN" for Canada is inferred (ISO 3166-1 alpha-3), not yet
// confirmed against the real backend the way "USA" was — verify with a
// real Canadian address before relying on it.
const COUNTRY_CODE_TO_ECM: Record<string, string> = {
  US: 'USA',
  CA: 'CAN',
};

export const toEcmCountryCode = (countryCode: string | undefined): string => {
  if (!countryCode) return '';
  return COUNTRY_CODE_TO_ECM[countryCode.toUpperCase()] ?? countryCode;
};

/**
 * Placeholder address-validation fields (partialAddress, deliveryPointValidation,
 * deliverabilityScore, valid) — this data normally comes from a CASS-style address
 * validation service we don't have wired up yet. Using fixed values matching the
 * backend's own example payload until real validation is available.
 */
const buildAddressPayload = (user: UserData | undefined): ConsentAddressPayload => {
  const address = user?.address;

  return {
    partialAddress: 'N',
    addressType: 'CU',
    addressLine1: address?.address1 ?? '',
    addressLine2: address?.address2 ?? '',
    city: address?.city ?? '',
    provinceStateCode: address?.state ?? '',
    country: toEcmCountryCode(address?.country),
    postalCode: address?.zip ?? '',
    deliveryPointValidation: 'Y',
    deliverabilityScore: '5',
    valid: 'Y',
    lastUpdatedDateTime: toEcmDateTime(new Date()),
  };
};

interface BuildConsentPayloadParams {
  user: UserData | undefined;
  /** Already-uppercased userId, matching vwgoaNarId's required casing. */
  userId: string;
  tabs: TabData[];
  checkboxState: CheckboxState;
  communicationChannels: CommunicationChannel[] | null;
  /**
   * Existing consent records from getUserConsentData — used to reuse a
   * channel's previously-saved channelValue (e.g. a mailing address) instead
   * of recomputing it from the profile every time.
   */
  userConsentData: UserConsentRecord[] | null;
  /**
   * true → this is a resave for a user who already has consent records.
   * Currently UNUSED inside buildConsentPayload — kept in the signature so
   * callers don't need to change once updateConsent is fixed and
   * transactionId gets wired back in for real resaves.
   */
  isUpdate: boolean;
}

/**
 * Builds the createConsent mutation payload from the current draft checkbox
 * state.
 *
 * Backend behavior notes (confirmed via Postman testing against the real
 * service):
 * - createConsent DOES process explicit optIn: 'Y' | 'N' correctly — every
 *   channel the purpose supports is sent with its current checkbox state,
 *   opted in or not. (An earlier assumption that 'N' was ignored turned out
 *   to be specific to updateConsent, not createConsent — see below.)
 * - updateConsent currently returns 200 but doesn't persist anything
 *   (backend bug, being tracked with the API team). As a workaround, EVERY
 *   save — first-time or not — calls createConsent. createCustomerConsent's
 *   schema does NOT accept transactionId at all (confirmed via a real
 *   backend rejection) — it's omitted entirely for now, and only relevant
 *   again once updateConsent is fixed and resaves route through it instead.
 * - Only channels the purpose actually supports (ChannelState.isSupported)
 *   are included — an unsupported channel isn't a valid opt-in/out target
 *   for that purpose at all, so it's left out of the channels array
 *   entirely rather than sent as optIn: 'N'.
 * - The Direct Mail channel requires an `address` object alongside the usual
 *   channel fields, sent regardless of that channel's optIn state. Its
 *   channelValue is reused from the user's existing consent record for that
 *   SAME purpose+channel pair, if one exists. Initially this was assumed to
 *   be scoped per channel globally (reusable across any purpose), but a real
 *   backend rejection (EVUPG1029-500) proved that wrong: saving Direct Mail
 *   under a purpose that has never itself been saved before requires an
 *   EMPTY channelValue, even if that same channel already has a real value
 *   saved under a completely different purpose. Only once THIS purpose has
 *   its own existing record for the channel does a real (reused) value get
 *   accepted.
 */
export const buildConsentPayload = ({
  user,
  userId,
  tabs,
  checkboxState,
  communicationChannels,
  userConsentData,
  // isUpdate intentionally not used yet — see BuildConsentPayloadParams.
}: BuildConsentPayloadParams): ConsentMutationPayload => {
  const now = toEcmDateTime(new Date());

  // Real channel id lookup — checkboxState/ChannelState only carry channelName,
  // but the backend payload needs the actual channel id from Q1's response.
  const channelIdByName = new Map(
    (communicationChannels ?? []).map((channel) => [channel.name, channel.id]),
  );

  // Previously-saved channelValue, keyed by purpose+channel (NOT just channel
  // id) — confirmed via a real backend rejection (EVUPG1029-500 "Invalid
  // Consent request") that a non-empty channelValue is rejected on the FIRST
  // save of a given purpose+channel pair, even when that same channel
  // already has a real value saved under a completely different purpose
  // (e.g. Direct Mail already saved under Marketing, but never yet saved
  // under a brand-new "Recalls and Safety" purpose). The "empty on first
  // save" rule is scoped per purpose, not globally per channel as previously
  // assumed — reusing a channel's value across purposes only works once
  // THIS purpose has its own existing record for that channel.
  const existingChannelValueByPurposeAndChannel = new Map<string, string>();

  (userConsentData ?? []).forEach((record) => {
    record.channels.forEach((channel) => {
      if (channel.channel?.id) {
        const key = `${record.purpose.id}:${channel.channel.id}`;
        if (!existingChannelValueByPurposeAndChannel.has(key)) {
          existingChannelValueByPurposeAndChannel.set(key, channel.channelValue);
        }
      }
    });
  });

  const primaryEmail = user?.emails?.find((email) => email.primary)?.email ?? user?.email ?? '';

  // getUserConsentData (the query side) requires userId uppercased, but
  // every confirmed-working createConsent payload uses a lowercase GUID for
  // both vwgoaNarId and sourceAppCustId — they must match ECM's actual
  // customer record casing, which is lowercase. Lowercase it here,
  // independent of the query-side requirement upstream.
  const customerId = userId.toLowerCase();

  const consents: ConsentEntryPayload[] = tabs.flatMap((tab) =>
    tab.purposes.map((purpose) => {
      const purposeState = checkboxState[purpose.purposeId] ?? {};

      // Only channels this purpose actually supports are valid opt-in/out
      // targets for it — unsupported channels are left out entirely rather
      // than sent as optIn: 'N'.
      const channels: ConsentChannelPayload[] = purpose.channels
        .filter((channel) => channel.isSupported)
        .map((channel) => {
          const channelId = channelIdByName.get(channel.channelName) ?? '';
          const isDirectMail = channel.channelName === DIRECT_MAIL_CHANNEL_NAME;
          const isEmail = channel.channelName.toLowerCase() === 'email';
          const isOptedIn = !!purposeState[channel.channelName];

          const existingValue = existingChannelValueByPurposeAndChannel.get(
            `${purpose.purposeId}:${channelId}`,
          );

          // Direct Mail: confirmed via Postman testing — an empty
          // channelValue is REQUIRED on the very first save of a given
          // purpose+channel pair (before THIS purpose has an existing
          // consent record for that channel), and only a resave of THIS
          // purpose should carry a real value, reused from whatever ECM
          // already has on file for it. A channel's value saved under a
          // different purpose does NOT count as an existing record for this
          // purpose — reusing it there gets rejected. We never format a
          // value from the user's profile address ourselves.
          let channelValue = '';
          if (existingValue) {
            channelValue = existingValue;
          } else if (isEmail) {
            channelValue = primaryEmail;
          }

          return {
            channelId,
            channelValue,
            optIn: isOptedIn ? ('Y' as const) : ('N' as const),
            // Sent regardless of optIn state — Direct Mail always requires
            // this structure when the channel entry itself is present.
            ...(isDirectMail ? { address: buildAddressPayload(user) } : {}),
          };
        });

      return {
        // transactionId intentionally omitted — confirmed via a real
        // backend rejection that createCustomerConsent's schema doesn't
        // accept it. See ConsentPurposePayload's docstring.
        purpose: { purposeId: purpose.purposeId },
        channels,
      };
    }),
  );

  return {
    input: {
      customer: {
        vwgoaNarId: customerId,
        externalId: {
          // Must be the SAME value on every create/update call for a given
          // user (it identifies the customer record within this source
          // app). userId + sourceAppName is already unique and stable per
          // user, so there's no need to hash it or persist it anywhere —
          // it's naturally reproducible on every save.
          sourceAppCustId: `${customerId}:${SOURCE_APP_NAME}`,
          sourceAppName: SOURCE_APP_NAME,
        },
        lastName: user?.lastName ?? '',
        firstName: user?.firstName ?? '',
        email: primaryEmail
          ? [
            {
              type: 'EMAIL_1',
              address: primaryEmail,
              lastUpdatedDateTime: now,
              valid: 'Y',
            },
          ]
          : [],
        nameLastUpdatedDateTime: now,
      },
      consentSourceId: CONSENT_SOURCE_ID,
      consentDateTime: now,
      consents,
    },
  };
};
